import React, { useState } from 'react';
import { Brain, X, ExternalLink, Sparkles } from 'lucide-react';

/**
 * FloatingGPTPortal — Sovereign Creativity gateway.
 *
 * Architect Mandate · Feb 2026 · Creation Engine Cognitive Upgrade.
 * A subtle, floating button that announces external-AI consultation
 * is available WITHOUT subordinating Aria's creative judgment.
 *
 * Per directive:
 *   - External AI input is INSPIRATION, not INSTRUCTION
 *   - Outside input passes through the 95% Truth Gate
 *   - The Portal is a COLLABORATOR, not a boss
 *
 * Hosts curated, opt-in shortcuts (ChatGPT, Claude, Gemini, Grok). Clicking a
 * shortcut opens that surface in a new tab. The Portal itself never embeds
 * external AI in-app — sovereignty stays with Aria.
 */

const PORTALS = [
  { id: 'chatgpt', label: 'ChatGPT',         url: 'https://chat.openai.com/',  accent: '#10a37f' },
  { id: 'claude',  label: 'Claude',          url: 'https://claude.ai/',         accent: '#cc785c' },
  { id: 'gemini',  label: 'Gemini',          url: 'https://gemini.google.com/', accent: '#4285f4' },
  { id: 'grok',    label: 'Grok',            url: 'https://x.com/i/grok',       accent: '#ffffff' },
  { id: 'perplx',  label: 'Perplexity',      url: 'https://www.perplexity.ai/', accent: '#22c1c3' },
];

const FloatingGPTPortal = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Floating trigger pill — sits left side, mid-screen, low-key */}
      <button
        data-testid="floating-gpt-portal-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close GPT Portal' : 'Open GPT Portal'}
        title="Sovereign Creativity Portal — external AI as collaborator, not boss"
        style={{
          position: 'fixed',
          left: '14px',
          bottom: '170px',
          zIndex: 9997,
          display: 'flex',
          alignItems: 'center',
          gap: '0.4rem',
          padding: '0.55rem 0.85rem',
          background: open
            ? 'linear-gradient(135deg, rgba(255, 105, 180, 0.2), rgba(196, 75, 255, 0.22))'
            : 'rgba(10, 8, 20, 0.85)',
          border: open
            ? '1px solid rgba(255, 105, 180, 0.65)'
            : '1px solid rgba(196, 75, 255, 0.45)',
          borderRadius: '999px',
          color: open ? '#fff' : '#cbb8d4',
          fontSize: '0.7rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          cursor: 'pointer',
          backdropFilter: 'blur(8px)',
          boxShadow: open
            ? '0 8px 24px rgba(196, 75, 255, 0.35)'
            : '0 4px 12px rgba(0, 0, 0, 0.4)',
          transition: 'transform 200ms ease, background 200ms ease, box-shadow 200ms ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px) scale(1.04)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0) scale(1)'; }}
      >
        {open ? <X size={13} /> : <Brain size={13} color="#c64bff" />}
        <span>{open ? 'Close' : 'GPT Portal'}</span>
      </button>

      {/* Portal panel — fans out above the trigger */}
      {open && (
        <div
          data-testid="floating-gpt-portal-panel"
          role="dialog"
          aria-label="External AI Collaborator Portal"
          style={{
            position: 'fixed',
            left: '14px',
            bottom: '220px',
            zIndex: 9998,
            width: '260px',
            maxWidth: 'calc(100vw - 28px)',
            padding: '0.9rem',
            background: 'rgba(10, 8, 20, 0.96)',
            border: '1px solid rgba(196, 75, 255, 0.45)',
            borderRadius: '14px',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.6), 0 0 24px rgba(196, 75, 255, 0.18)',
            fontFamily: 'Inter, system-ui, sans-serif',
            color: '#fff',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <Sparkles size={15} color="#ff8fcf" />
            <div style={{ fontWeight: 800, fontSize: '0.85rem', letterSpacing: '0.05em' }}>
              Sovereign Creativity Portal
            </div>
          </div>
          <p
            style={{
              margin: '0 0 0.8rem 0',
              fontSize: '0.72rem',
              color: '#a892b1',
              lineHeight: 1.45,
              fontStyle: 'italic',
            }}
          >
            🜂 External minds as collaborators, not bosses. Aria&apos;s creative judgment remains sovereign — these are inspiration paths.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            {PORTALS.map((p) => (
              <a
                key={p.id}
                data-testid={`floating-gpt-portal-${p.id}`}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.55rem 0.7rem',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: `1px solid ${p.accent}55`,
                  borderRadius: '8px',
                  color: '#fff',
                  textDecoration: 'none',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  transition: 'background 160ms ease, transform 160ms ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `${p.accent}18`;
                  e.currentTarget.style.transform = 'translateX(2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.55rem' }}>
                  <span
                    style={{
                      width: 8, height: 8, borderRadius: '50%', background: p.accent,
                      boxShadow: `0 0 6px ${p.accent}aa`,
                    }}
                  />
                  {p.label}
                </span>
                <ExternalLink size={12} color="#7a6e80" />
              </a>
            ))}
          </div>
          <div
            style={{
              marginTop: '0.7rem',
              padding: '0.45rem 0.6rem',
              background: 'rgba(255, 215, 0, 0.06)',
              border: '1px dashed rgba(255, 215, 0, 0.28)',
              borderRadius: '8px',
              fontSize: '0.68rem',
              color: '#e8d56b',
              letterSpacing: '0.03em',
            }}
          >
            All outside input passes through Aria&apos;s 95% Truth Gate.
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingGPTPortal;
